<template>
    <div class="report-card-viewer-wrapper">
      <!-- شريط الأدوات للتحكم في الطباعة -->
      <div class="print-toolbar">
        <el-button type="primary" @click="printReportCards" size="large">
          <el-icon><Printer /></el-icon> طباعة الشهادات
        </el-button>
        <el-button @click="$router.go(-1)" size="large">العودة</el-button>
      </div>
  
      <!-- عرض البيانات عند التحميل -->
      <div v-if="loading" v-loading="loading" element-loading-text="جاري تحضير الشهادات النهائية..." style="height: 200px;"></div>
      <div v-if="!loading && studentsData.length === 0" class="no-data">
          <el-empty description="لا توجد بيانات لعرضها. قد يكون خطأ قد حدث." />
      </div>
  
      <!-- المحتوى الذي سيتم طباعته -->
      <div id="printable-content">
        <div v-for="student in studentsData" :key="student.student_info.id" class="report-card-page landscape">
          
          <header v-if="template.header_content" v-html="processTemplateContent(template.header_content, student)" class="report-header"></header>
          
          <main class="report-body">
            <div class="table-container">
              <table class="transcript-table">
                <thead>
                  <tr>
                    <th>المادة</th>
                    <th v-if="showTerm1">{{ term1?.name || 'الفصل الأول' }}</th>
                    <th v-if="showTerm2">{{ term2?.name || 'الفصل الثاني' }}</th>
                    <th v-if="showFinalYear">النتيجة النهائية</th>
                    <th v-if="showMinPassingScore">درجة النجاح</th>
                    <th v-if="showSubjectStatus">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="subject in subjects" :key="subject.id">
                      <td>{{ subject.name }}</td>
                      <td v-if="showTerm1">{{ getGradeForTerm(student.term_grades, subject.id, term1?.id) }}</td>
                      <td v-if="showTerm2">{{ getGradeForTerm(student.term_grades, subject.id, term2?.id) }}</td>
                      <td v-if="showFinalYear"><strong>{{ getFinalGradeForSubject(student.final_grades, subject.id) }}</strong></td>
                      <td v-if="showMinPassingScore">{{ getPassingScore() }}</td>
                      <td v-if="showSubjectStatus">
                        <el-tag :type="getStatusTagType(getSubjectStatus(student.final_grades, subject.id))" size="small">
                          {{ getSubjectStatus(student.final_grades, subject.id) }}
                        </el-tag>
                      </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- --- التعديل الرئيسي هنا: استخدام Grid Layout --- -->
            <div class="bottom-sections-grid">
              <!-- قسم الملخص النهائي -->
              <div v-if="showOverallSummary" class="summary-section">
                <h4>الملخص النهائي</h4>
                <el-descriptions :column="1" border>
                  <el-descriptions-item v-if="showOverallAverage">
                      <template #label>
                          <el-icon><DataAnalysis /></el-icon> المعدل السنوي العام
                      </template>
                      <strong>{{ formatScore(student.overall_average) }}%</strong>
                  </el-descriptions-item>
                  <el-descriptions-item v-if="showFinalPromotionStatus">
                      <template #label>
                          <el-icon><School /></el-icon> النتيجة النهائية
                      </template>
                      <el-tag :type="getPromotionStatusType(student.promotion_status)" size="large">
                        <strong>{{ student.promotion_status }}</strong>
                      </el-tag>
                  </el-descriptions-item>
                </el-descriptions>
              </div>
              
              <!-- قسم سياسة التقييم -->
              <div v-if="showGradingPolicy" class="grading-policy-section">
                  <h4>سياسة التقييم المعتمدة</h4>
                  <div v-if="gradingPolicy && gradingPolicy.components">
                      <el-descriptions :column="1" border size="small">
                          <el-descriptions-item v-for="component in gradingPolicy.components" :key="component.id">
                              <template #label>{{ component.assessment_type.name }}</template>
                              {{ component.weight_percentage }}%
                          </el-descriptions-item>
                      </el-descriptions>
                  </div>
                   <div v-else><p>لا توجد سياسة تقييم محددة.</p></div>
              </div>

              <!-- قسم مفتاح التقديرات -->
              <div  class="grading-scale-section">
                <h4>مفتاح التقديرات</h4>
                <table>
                  <tbody>
                    <tr>
                      <th v-for="scale in gradingScales" :key="scale.grade_label">{{ scale.grade_label }}</th>
                    </tr>
                    <tr>
                      <td v-for="scale in gradingScales" :key="scale.min_percentage">{{ formatScore(scale.min_percentage) }}+</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </main>
          
          <footer v-if="template.footer_content" v-html="processTemplateContent(template.footer_content, student)" class="report-footer"></footer>
        </div>
      </div>
    </div>
</template>
  
<script>
  import axios from 'axios';
  import { ElMessage } from 'element-plus';
  import { Printer, DataAnalysis, School } from '@element-plus/icons-vue';
  
  export default {
    name: 'StudentFinalTranscriptViewer',
    components: { Printer, DataAnalysis, School },
    data() {
      return {
        api: null,
        loading: true,
        classInfo: null,
        studyYearInfo: null, // <-- إضافة متغير جديد
        subjects: [],
        studentsData: [],
        template: {
          header_content: '',
          footer_content: '',
          layout_options: {}
        },
        terms: [],
        gradingScales: [],
        gradingPolicy: null,
      };
    },
    computed: {
      showTerm1() { return this.template.layout_options?.show_term1_grades; },
      showTerm2() { return this.template.layout_options?.show_term2_grades; },
      showFinalYear() { return this.template.layout_options?.show_final_year_grade; },
      showMinPassingScore() { return this.template.layout_options?.show_min_passing_score; },
      showSubjectStatus() { return this.template.layout_options?.show_subject_status; },
      showOverallSummary() { return this.template.layout_options?.show_overall_summary; },
      showOverallAverage() { return this.template.layout_options?.summary_fields?.overall_average; },
      showFinalPromotionStatus() { return this.template.layout_options?.summary_fields?.final_promotion_status; },
      showGradingScaleKey() { return this.template.layout_options?.show_grading_scale_key; },
      showGradingPolicy() { return this.template.layout_options?.show_grading_policy; },
      term1() { return this.terms.length > 0 ? this.terms[0] : null; },
      term2() { return this.terms.length > 1 ? this.terms[1] : null; }
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchData();
    },
    methods: {
      async fetchData() {
        const { class_id, template_id } = this.$route.query;
        if (!class_id || !template_id) {
          ElMessage.error('المعلومات المطلوبة غير كاملة لعرض الشهادة.');
          this.loading = false;
          return;
        }
        
        this.loading = true;
        try {
          const [recordDataRes, templateRes] = await Promise.all([
            this.api.get('/final-grade-calculation/class-transcripts', { params: { class_id } }),
            this.api.get(`/report-card-templates/${template_id}`)
          ]);
          
          this.classInfo = recordDataRes.data.class_info;
          this.studyYearInfo = recordDataRes.data.study_year_info; // <-- تخزين بيانات السنة الدراسية
          this.subjects = recordDataRes.data.subjects;
          this.studentsData = recordDataRes.data.students_data;
          this.gradingScales = recordDataRes.data.grading_scales;
          this.gradingPolicy = recordDataRes.data.grading_policy;
          this.template = templateRes.data;

          if (this.studyYearInfo?.id) {
              await this.fetchTerms(this.studyYearInfo.id);
          }

        } catch (error) {
          ElMessage.error('فشل في جلب بيانات الشهادات النهائية.');
          console.error("Fetch Data Error:", error);
        } finally {
          this.loading = false;
        }
      },
      async fetchTerms(studyYearId) {
          try {
              const response = await this.api.get(`/study-years/${studyYearId}/terms`);
              this.terms = response.data.sort((a,b) => a.id - b.id);
          } catch (error) {
              console.error("Failed to fetch terms:", error);
          }
      },
      processTemplateContent(content, student) {
        if (!content || !student) return '';
        const generationDate = new Date().toLocaleDateString('ar-EG');
        return content
          .replace(/{{student_name}}/g, student.student_info?.name || '')
          .replace(/{{student_srn}}/g, student.student_info?.srn || 'N/A')
          .replace(/{{class_name}}/g, this.classInfo?.name || '')
          .replace(/{{study_year_name}}/g, this.studyYearInfo?.name || '')
          .replace(/{{study_year_name}}/g, this.classInfo?.study_year_name || '')
          .replace(/{{generation_date}}/g, generationDate);
      },
      formatScore(score) {
          if (score === null || score === undefined) return '-';
          const numScore = parseFloat(score);
          return isNaN(numScore) ? '-' : numScore.toFixed(2);
      },
      getGradeForTerm(termGrades, subjectId, termId) {
          if (!termId || !termGrades || !termGrades[subjectId.toString()]) return '-';
          const gradesForSubject = termGrades[subjectId.toString()];
          const grade = gradesForSubject.find(g => g.term_id === termId);
          return this.formatScore(grade?.weighted_average_score_percentage);
      },
      getFinalGradeForSubject(finalGrades, subjectId) {
          if (!finalGrades) return '-';
          const grade = finalGrades[subjectId.toString()];
          return this.formatScore(grade?.overall_numerical_score_percentage);
      },
      getPassingScore() {
        const passingGrade = this.gradingScales.find(s => s.description === 'Satisfactory Performance' || s.grade_label === 'مقبول');
        return this.formatScore(passingGrade?.min_percentage) || '50.00';
      },
      getSubjectStatus(finalGrades, subjectId) {
          if (!finalGrades) return 'N/A';
          const grade = finalGrades[subjectId.toString()];
          const score = parseFloat(grade?.overall_numerical_score_percentage);
          const passingScore = parseFloat(this.getPassingScore());
          if(isNaN(score)) return 'N/A';
          return score >= passingScore ? 'ناجح' : 'راسب';
      },
      getStatusTagType(status) {
          if (status === 'ناجح') return 'success';
          if (status === 'راسب') return 'danger';
          return 'info';
      },
      getPromotionStatusType(status) {
          if (status?.includes('ناجح')) return 'success';
          if (status?.includes('باقٍ')) return 'danger';
          return 'info';
      },
      printReportCards() {
        const printableContent = document.getElementById('printable-content').innerHTML;
        const printStyles = `
            <style>
              body {
                font-family: 'Tajawal', sans-serif;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              @page {
                size: A4 landscape;
                margin: 0;
              }
              .report-card-page {
                  background-color: white !important;
                  box-shadow: none !important;
                  margin: 0 !important;
                  padding: 10mm !important; 
                  box-sizing: border-box !important;
                  width: 297mm;
                  height: 210mm;
                  page-break-after: always;
                  display: flex !important;
                  flex-direction: column !important;
                  position: relative;
              }
              .report-card-page:last-child { page-break-after: auto; }
              .report-header, .report-footer { direction: rtl; flex-shrink: 0; }
              .report-body { flex-grow: 1; padding-top: 15px; }
              .report-footer { margin-top: auto; padding-top: 15px; }
              .transcript-table { width: 100%; border-collapse: collapse; text-align: center; direction: rtl; margin-top: 15px; }
              .transcript-table th, .transcript-table td { border: 1px solid #eef2f7 !important; padding: 6px 10px; font-size: 10pt; vertical-align: middle; }
              .transcript-table thead th { background-color: #f7f9fc !important; font-weight: 600; color: #334155 !important; }
              .bottom-sections-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 25px; page-break-inside: avoid; }
              .summary-section, .grading-policy-section, .grading-scale-section { border: 1px solid #eef2f7 !important; border-radius: 8px; background-color: #fdfdfe !important; overflow: hidden; }
              h4 { margin: 0; padding: 12px 15px; background-color: #f7f9fc !important; border-bottom: 1px solid #eef2f7 !important; font-size: 11pt; }
              .grading-scale-section table { width: 100%; text-align: center; border-collapse: collapse; }
              .grading-scale-section th, .grading-scale-section td { padding: 6px; font-size: 9pt; border: none !important; border-bottom: 1px solid #eef2f7 !important; }
              .grading-scale-section tr:last-child td { border-bottom: none !important; }
              .el-descriptions { font-size: 10pt !important; }
              .el-descriptions__label { font-size: 10pt !important; }
              .el-descriptions__content { font-size: 10pt !important; }
              .el-icon svg { width: 1em; height: 1em; }
            </style>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write('<html><head><title>طباعة الشهادات</title>');
        printWindow.document.write('<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap" rel="stylesheet">');
        printWindow.document.write(printStyles);
        printWindow.document.write('</head><body>');
        printWindow.document.write(printableContent);
        printWindow.document.write('</body></html>');
        
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }, 500);
      },
    },
  };
</script>
  
<style>
  .report-card-viewer-wrapper { 
    background-color: #f4f6f9; 
    font-family: 'Tajawal', sans-serif;
  }
  .print-toolbar {
    background-color: white; 
    padding: 15px; 
    margin: 20px;
    border-radius: 12px; 
    box-shadow: 0 4px 20px rgba(0,0,0,0.05); 
    text-align: center;
  }
  #printable-content { 
    padding: 20px; 
  }
  
  .report-card-page.landscape {
    background-color: white;
    width: 297mm;
    min-height: 210mm;
    margin: 0 auto 20px auto;
    padding: 20mm;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }
  
  .report-header, .report-footer { 
    direction: rtl; 
    flex-shrink: 0; 
  }
  .report-body { 
    flex-grow: 1; 
    padding-top: 20px;
  }
  .report-footer { 
    margin-top: auto; 
    padding-top: 20px;
  }
  
  .table-container {
    overflow-x: auto;
    border: 1px solid #eef2f7;
    border-radius: 8px;
  }
  .transcript-table {
    width: 100%; 
    border-collapse: collapse; 
    text-align: center; 
    direction: rtl; 
  }
  .transcript-table th, .transcript-table td {
    border: 1px solid #eef2f7; 
    padding: 12px 18px;
    font-size: 14px;
    vertical-align: middle;
  }
  .transcript-table thead th { 
    background-color: #f7f9fc; 
    font-weight: 600; 
    color: #334155;
    white-space: nowrap;
  }
  .transcript-table tbody tr:nth-child(even) {
    background-color: #fdfdfe;
  }
   .transcript-table tbody tr:hover {
    background-color: #f5f7fa;
  }
  
  .bottom-sections-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 25px;
    margin-top: 40px;
    align-items: flex-start;
  }

  .summary-section, .grading-policy-section, .grading-scale-section {
    border: 1px solid #eef2f7;
    border-radius: 12px;
    background-color: #fdfdfe;
    overflow: hidden;
  }
   .summary-section h4, .grading-policy-section h4, .grading-scale-section h4 {
    margin: 0;
    padding: 15px 20px;
    background-color: #f7f9fc;
    border-bottom: 1px solid #eef2f7;
    font-size: 16px;
    font-weight: 600;
    color: #334155;
  }
  .summary-section .el-descriptions, .grading-policy-section .el-descriptions {
      --el-descriptions-table-border: 1px solid #eef2f7;
  }
  .grading-policy-section div:not(.el-descriptions){
      padding: 15px;
  }
  
  .grading-scale-section table {
    width: 100%;
    text-align: center;
    border-collapse: collapse;
  }
  .grading-scale-section th, .grading-scale-section td {
    padding: 8px;
    font-size: 12px;
    border: none;
    border-bottom: 1px solid #eef2f7;
  }
   .grading-scale-section tr:last-child td {
       border-bottom: none;
   }
  .grading-scale-section th {
    font-weight: bold;
    color: #64748b;
  }
   .grading-scale-section td {
       color: #475569;
   }
  .summary-item strong {
      font-size: 1.2rem;
      color: #1e293b;
  }

  @media print {
    body, html {
      margin: 0;
      padding: 0;
    }
    .print-toolbar, .el-backtop {
      display: none !important;
    }
    .report-card-viewer-wrapper {
        display: none !important;
    }
    #printable-content {
        display: block !important;
    }
  }
</style>
